import React from "react"
import { twMerge } from "tailwind-merge"

import AnimatedSection from "../../ProProfileShared/AnimatedSection"
import { DEFAULT_HIGHLIGHT_CLASSES } from "../constants"
import { TextLogoView } from "../sitewide-styles/components/TextLogoView"
import { getThemeColors } from "../utils/colorModeUtils"
import { getContrastSafeButtonTextColor, isDarkColor } from "../utils/colorUtils"
import { formatFontFamily } from "../utils/fontUtils"
import { applyButtonTheme, getButtonClasses } from "../utils/themeUtils"
import {
  buildServiceUrl,
  buildBookingUrl,
  buildEventTypeUrl,
  buildDefaultPageUrl,
  findBuilderPageByDefaultType,
  mergeBuilderNavLinks,
  normalizeBuilderNavHref,
  openExternalUrl,
  pageVisibleInBuilderNav,
  publicPathForBuilderPage,
  scopePath
} from "../utils/urlUtils"
import { useOptionalWebsiteBuilderV2 } from "../WebsiteBuilderV2Context"

const HeaderSectionDesktop = ({
  section,
  practice,
  theme,
  highlightedField,
  transparent = false,
  transparentBackgroundColor
}) => {
  // Context is null on public page (no Provider); use practice pages in that case
  const context = useOptionalWebsiteBuilderV2()
  const contextPages = context?.pages || []
  const textLogoBuilderEnabled = context?.textLogoBuilderEnabled ?? false
  const practicePages = practice?.pages || []
  const availablePages = contextPages.length > 0 ? contextPages : practicePages

  const isHighlighted = highlightedField === `section-${section.id}`
  const settings = section.settings || {}
  const showLogo = settings.showLogo || true
  const showCTA = settings.showCTA || true
  const ctaText = settings.ctaText || theme?.header?.buttonText || "Book Now"
  const ctaAction = theme?.header?.ctaAction || settings.ctaAction || "booking"
  const ctaTarget = theme?.header?.ctaTarget || settings.ctaTarget || ""
  const externalUrl = theme?.header?.externalUrl || settings.externalUrl || ""
  const showNavigation = settings.showNavigation || true

  // Promo banner settings from HeaderForm
  const showPromoBanner = theme?.header?.showPromoBanner || false
  const promoBannerText = theme?.header?.promoHeadline || "This is some exciting new event that we are offering!"
  const promoBannerLinkText = theme?.header?.promoButtonText || "Learn more"
  const promoBannerAction = theme?.header?.promoCtaType || "page"
  const promoBannerTarget = theme?.header?.promoService || ""
  const promoBannerEvent = theme?.header?.promoEvent || ""
  const promoExternalUrl = theme?.header?.promoExternalUrl || ""
  const promoBannerColorMode = theme?.header?.promoBannerColorMode || "white"

  // Get theme colors for promo banner
  const themeColors = getThemeColors(theme)

  // Resolve promo banner colors based on color mode
  const getPromoBannerColors = () => {
    switch (promoBannerColorMode) {
      case "dark":
        return {
          backgroundColor: themeColors.primary || "#BD654F",
          textColor: themeColors.textLight || "#FFFFFF",
          linkColor: themeColors.textLight || "#FFFFFF"
        }
      case "light":
        return {
          backgroundColor: themeColors.bgLight || "#F4ECE9",
          textColor: themeColors.textDark || "#55433F",
          linkColor: themeColors.primary || "#BD654F"
        }
      case "white":
      default:
        return {
          backgroundColor: "#FFFFFF",
          textColor: themeColors.textDark || "#55433F",
          linkColor: themeColors.primary || "#BD654F"
        }
    }
  }

  const promoBannerColors = getPromoBannerColors()

  const handleCTAClick = () => {
    switch (ctaAction) {
      case "page":
        if (ctaTarget) {
          // Use the practice slug to navigate to the correct page
          const targetUrl = scopePath(`/${ctaTarget}`, practice)
          window.location.href = targetUrl
        }
        break
      case "service":
        if (ctaTarget) {
          window.location.href = buildServiceUrl(ctaTarget, practice)
        }
        break
      case "booking":
        window.location.href = buildBookingUrl(practice)
        break
      case "contact":
        document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })
        break
      case "scroll":
        window.scrollBy({ top: window.innerHeight, behavior: "smooth" })
        break
      case "external":
        if (externalUrl || ctaTarget) {
          const url = externalUrl || ctaTarget
          if (url) window.open(url, "_blank", "noopener,noreferrer")
        }
        break
      default:
        break
    }
  }

  const handlePromoBannerClick = () => {
    switch (promoBannerAction) {
      case "page":
        if (promoBannerTarget) {
          // Use the practice slug to navigate to the correct page
          const targetUrl = scopePath(`/${promoBannerTarget}`, practice)
          window.location.href = targetUrl
        }
        break
      case "service":
        if (promoBannerTarget) {
          // It's a service, use the service URL
          window.location.href = buildServiceUrl(promoBannerTarget, practice)
        }
        break
      case "event":
        if (promoBannerEvent) {
          // Build event URL using the event type slug
          const eventUrl = buildEventTypeUrl(promoBannerEvent, practice)
          window.location.href = eventUrl
        }
        break
      case "booking":
        window.location.href = buildBookingUrl(practice)
        break
      case "contact":
        document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })
        break
      case "scroll":
        window.scrollBy({ top: window.innerHeight, behavior: "smooth" })
        break
      case "external":
        openExternalUrl(promoExternalUrl)
        break
      default:
        break
    }
  }

  const appendBlogNavLink = (links) => {
    const hasBlog = practice?.hasPublishedBlogPosts || practice?.has_published_blog_posts
    const blogLabel = practice?.blogPageTitle || practice?.blog_page_title || "Blog"
    const blogHref = buildDefaultPageUrl(practice, "blog", "blog")
    if (!hasBlog || links.some((link) => link.href === blogHref)) return links

    return [...links, { text: blogLabel, href: blogHref }]
  }

  const fallbackDefaultNavPages = [
    { defaultPageType: "services", label: "Services", legacySlug: "services" },
    { defaultPageType: "about", label: "About", legacySlug: "about" },
    { defaultPageType: "reviews", label: "Reviews", legacySlug: "review" },
    { defaultPageType: "contact", label: "Contact", legacySlug: "contact" }
  ]

  // Generate navigation links from practice's pages
  const generateNavigationLinks = () => {
    if (!availablePages || availablePages.length === 0) {
      const defaultLinks = fallbackDefaultNavPages.map(({ defaultPageType, label, legacySlug }) => {
        const page = findBuilderPageByDefaultType(practice, defaultPageType)
        return {
          text: page?.title || label,
          href: buildDefaultPageUrl(practice, defaultPageType, legacySlug)
        }
      })

      // Add Home link only if showHome is enabled
      const showHome = theme?.header?.showHome || false
      const links = showHome ? [{ text: "Home", href: scopePath("/", practice) }, ...defaultLinks] : defaultLinks
      return appendBlogNavLink(links)
    }

    // Start with Home link if enabled, then add other pages
    const showHome = theme?.header?.showHome || false
    const homeLink = showHome ? { text: "Home", href: scopePath("/", practice) } : null

    // Filter out the home page and hidden pages, then create navigation links from available pages
    const pageLinks = availablePages
      .filter((page) => page.defaultPageType !== "home" && pageVisibleInBuilderNav(page, practice))
      .sort((a, b) => (a.order || 0) - (b.order || 0)) // Sort by order (similar to sections)
      .map((page) => ({
        text: page.title,
        href: publicPathForBuilderPage(page, practice)
      }))

    const links = homeLink ? [homeLink, ...pageLinks] : pageLinks
    return appendBlogNavLink(links)
  }

  const navigationLinks = generateNavigationLinks()

  // Merge legacy stored nav with dynamic page links so custom pages appear like footer nav.
  const headerNavigationLinks = mergeBuilderNavLinks(theme?.header?.navigationLinks, navigationLinks, practice)

  // Determine text color for transparent header based on background
  // When transparent, check if the background behind is light or dark
  const getTransparentTextColor = () => {
    if (!transparent) return null

    // If caller tells us what’s behind the transparent header, use it.
    // Otherwise, keep legacy behavior: transparent headers are assumed to sit on dark hero imagery.
    if (!transparentBackgroundColor) return theme?.colors?.textLight || "#FFFFFF"

    const isBackgroundDark = isDarkColor(transparentBackgroundColor)
    return isBackgroundDark ? theme?.colors?.textLight || "#FFFFFF" : theme?.colors?.textDark || "#55433f"
  }

  const transparentTextColor = getTransparentTextColor()

  // Helper function to determine if a link is active
  const isLinkActive = (href) => {
    if (typeof window === "undefined") return false
    const currentPath = window.location.pathname
    // Normalize paths: remove leading/trailing slashes and convert to arrays
    const normalizePath = (path) =>
      (typeof path === "string" ? path : "")
        .replace(/^\/+|\/+$/g, "")
        .split("/")
        .filter(Boolean)
    const currentParts = normalizePath(currentPath)
    const hrefParts = normalizePath(href)

    // If href is empty or just "/", check if we're exactly on home (no sub-routes)
    if (hrefParts.length === 0) {
      return currentParts.length === 0
    }

    // Special case: if href is just the practice slug (e.g., "/peaceful-living"),
    // it should only be active when we're exactly on that path, not on sub-routes
    // This handles the "Home" link case
    if (hrefParts.length === 1) {
      // Check if this looks like a home link (just practice slug)
      // If current path has more parts, it's not the home page
      if (currentParts.length !== 1) return false
      // Compare the single segment
      return currentParts[0] === hrefParts[0]
    }

    // For other links with multiple segments, check if current path starts with href path
    if (currentParts.length < hrefParts.length) return false

    // Compare path segments
    for (let i = 0; i < hrefParts.length; i++) {
      if (currentParts[i] !== hrefParts[i]) return false
    }

    return true
  }

  // Helper function to get navigation link styles based on state
  const getNavLinkStyles = (link, isActive) => {
    // When transparent, use the calculated text color based on background
    // Otherwise, determine if dark based on theme background
    const isDark = transparent
      ? isDarkColor(transparentBackgroundColor || "#000000")
      : theme?.colors?.background === theme?.colors?.bgDark

    const textColor =
      transparent && transparentTextColor
        ? transparentTextColor
        : isDark
        ? theme?.colors?.textLight || "#FFFFFF"
        : theme?.colors?.textDark || "#55433f"

    const baseStyles = {
      fontFamily: formatFontFamily(theme?.fonts?.paragraph),
      fontSize: "16px",
      lineHeight: "24px",
      paddingTop: "8px",
      paddingBottom: "8px",
      textDecoration: "none",
      transition: "all 0.2s ease"
    }

    // Light theme styles
    if (!isDark) {
      if (isActive) {
        return {
          ...baseStyles,
          fontWeight: "600",
          color: textColor,
          borderBottom: `2px solid ${theme?.colors?.primary || "#bd654f"}`,
          paddingBottom: "6px" // Adjust for 2px border
        }
      }
      return {
        ...baseStyles,
        fontWeight: "400",
        color: textColor
      }
    }

    // Dark theme styles
    if (isActive) {
      return {
        ...baseStyles,
        fontWeight: "600",
        color: textColor,
        borderBottom: `2px solid ${textColor}`,
        paddingBottom: "6px" // Adjust for 2px border
      }
    }
    return {
      ...baseStyles,
      fontWeight: "400",
      color: textColor
    }
  }

  // Helper function to get hover styles
  const getNavLinkHoverStyles = (isActive) => {
    const isDark = transparent
      ? isDarkColor(transparentBackgroundColor || "#000000")
      : theme?.colors?.background === theme?.colors?.bgDark

    const hoverColor =
      transparent && transparentTextColor
        ? transparentTextColor
        : isDark
        ? theme?.colors?.textLight || "#FFFFFF"
        : theme?.colors?.textDark || "#55433f"

    if (isActive) return {} // Active links already have underline

    if (!isDark) {
      return {
        borderBottom: `1px solid ${theme?.colors?.primary || "#bd654f"}`,
        paddingBottom: "7px" // Adjust for 1px border
      }
    }
    return {
      borderBottom: `1px solid ${hoverColor}`,
      paddingBottom: "7px" // Adjust for 1px border
    }
  }

  return (
    <div className="relative z-10">
      {/* Promo Banner - Separate from header section */}
      {showPromoBanner && (
        <div
          className="flex w-full items-center justify-center px-8 py-4 text-center"
          style={{
            backgroundColor: promoBannerColors.backgroundColor,
            color: promoBannerColors.textColor
          }}>
          <div className="flex items-center gap-2">
            <span
              className="text-base font-normal leading-6"
              style={{
                fontFamily: formatFontFamily(theme?.fonts?.paragraph),
                fontSize: "16px",
                lineHeight: "24px"
              }}>
              {promoBannerText}{" "}
              <button
                onClick={handlePromoBannerClick}
                className="font-semibold underline hover:no-underline"
                style={{
                  fontFamily: formatFontFamily(theme?.fonts?.paragraph),
                  color: promoBannerColors.linkColor
                }}>
                {promoBannerLinkText}
              </button>
            </span>
          </div>
        </div>
      )}

      {/* Main Header Section */}
      {/* eslint-disable-next-line tailwindcss/no-custom-classname */}
      <section
        className={twMerge(
          "header-section-desktop relative flex items-center justify-center",
          transparent ? "bg-transparent" : "",
          isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
        )}
        style={{
          backgroundColor: transparent ? "transparent" : theme?.colors?.background || "#FFFFFF",
          minHeight: "72px",
          height: showLogo ? `${(theme?.header?.logoSize || 72) + 20}px` : "72px"
        }}>
        <AnimatedSection className="w-full px-8 py-3">
          <div className="flex items-center justify-between">
            {/* Left Side - Logo */}
            {showLogo && (
              <div className="flex items-center">
                <a href={scopePath("/", practice)} className="cursor-pointer transition-opacity hover:opacity-75">
                  {/* Text logo only when flag on and theme has logoType/textLogo; existing themes keep image/name */}
                  {textLogoBuilderEnabled && theme?.header?.logoType === "text" && theme?.header?.textLogo ? (
                    (() => {
                      const tl = theme?.header?.textLogo
                      const logoSize = theme?.header?.logoSize ?? 72
                      // Honor user-picked font sizes verbatim. Only the fallback (legacy
                      // themes without explicit sizes) is derived from logoSize.
                      const mainFontSize = tl?.mainFontSize ?? Math.round(logoSize * 0.22)
                      const secondFontSize = tl?.secondFontSize
                      return (
                        <TextLogoView
                          mainLine={tl?.mainLine || practice?.name || "Practice Name"}
                          secondLine={tl?.secondLine}
                          mainFont={tl?.mainFont || "Inter"}
                          secondFont={tl?.secondFont || "Inter"}
                          color={
                            transparent && transparentTextColor
                              ? transparentTextColor
                              : transparent
                              ? "#FFFFFF"
                              : theme?.colors?.textDark || "#000000"
                          }
                          mainFontSize={`${mainFontSize}px`}
                          secondFontSize={secondFontSize != null ? `${secondFontSize}px` : undefined}
                          mainLetterSpacing={tl?.mainLetterSpacing}
                          secondLetterSpacing={tl?.secondLetterSpacing}
                          lineSpacing={tl?.lineSpacing}
                          alignment={tl?.alignment}
                        />
                      )
                    })()
                  ) : practice?.logoUrl || theme?.header?.logo ? (
                    <img
                      src={practice?.logoUrl || theme?.header?.logo}
                      alt={practice?.name || "Practice Logo"}
                      width={72}
                      height={40}
                      className="w-auto object-contain"
                      style={{
                        maxWidth: `${theme?.header?.logoSize || 72}px`,
                        height: "auto",
                        borderRadius: theme?.borderRadius || "0px"
                      }}
                    />
                  ) : (
                    <span
                      className="text-sm font-bold tracking-tight"
                      style={{
                        fontFamily: theme?.fonts?.heading || "Playfair Display, serif",
                        fontWeight: "700",
                        fontSize: "14.47px",
                        lineHeight: "1em",
                        letterSpacing: "-4%",
                        color:
                          transparent && transparentTextColor
                            ? transparentTextColor
                            : transparent
                            ? "#FFFFFF"
                            : theme?.colors?.textDark || "#000000"
                      }}>
                      {practice?.name || "Practice Name"}
                    </span>
                  )}
                </a>
              </div>
            )}

            {/* Right Side - Navigation and CTA with fixed positioning */}
            <div className="flex items-center gap-4">
              {/* Navigation Links */}
              {showNavigation && (
                <nav className="flex items-center gap-4">
                  {headerNavigationLinks.map((link, index) => {
                    const navHref = normalizeBuilderNavHref(link.href, practice)
                    const active = isLinkActive(navHref)
                    const baseStyles = getNavLinkStyles(link, active)
                    return (
                      <a
                        key={index}
                        href={navHref}
                        className="whitespace-nowrap px-0 transition-all"
                        style={baseStyles}
                        onMouseEnter={(e) => {
                          if (!active) {
                            const hoverStyles = getNavLinkHoverStyles(active)
                            Object.assign(e.target.style, hoverStyles)
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!active) {
                            e.target.style.borderBottom = "none"
                            e.target.style.paddingBottom = baseStyles.paddingBottom
                          }
                        }}>
                        {link.text}
                      </a>
                    )
                  })}
                </nav>
              )}
              {/* Main CTA */}
              {showCTA && (
                <button
                  type="button"
                  aria-label={ctaText || "Call to action"}
                  onClick={handleCTAClick}
                  className={twMerge(
                    "inline-flex items-center justify-center whitespace-nowrap px-40 py-[12px] text-base font-normal transition-all hover:shadow-lg",
                    getButtonClasses(theme?.buttonStyle)
                  )}
                  style={applyButtonTheme(
                    {
                      backgroundColor: theme?.colors?.primary || "#BD654F",
                      color: getContrastSafeButtonTextColor(theme?.colors?.primary || "#BD654F", theme),
                      fontFamily: formatFontFamily(theme?.fonts?.paragraph),
                      fontWeight: "400",
                      fontSize: "16px",
                      lineHeight: "24px"
                    },
                    theme
                  )}
                  onMouseEnter={(e) => {
                    const bg = theme?.colors?.primaryDark || "#A55A45"
                    e.target.style.backgroundColor = bg
                    e.target.style.color = getContrastSafeButtonTextColor(bg, theme)
                  }}
                  onMouseLeave={(e) => {
                    const bg = theme?.colors?.primary || "#BD654F"
                    e.target.style.backgroundColor = bg
                    e.target.style.color = getContrastSafeButtonTextColor(bg, theme)
                  }}>
                  {ctaText}
                </button>
              )}
            </div>
          </div>
        </AnimatedSection>
      </section>
    </div>
  )
}

export default HeaderSectionDesktop
